import { useState, useCallback } from "react";
import { Scales, ArrowsCounterClockwise, ClockCounterClockwise, ChatsCircle, Lightning, Binoculars } from "@phosphor-icons/react";
import PDFViewer from "./evaluate/PDFViewer";
import QAPanel from "./evaluate/QAPanel";
import EvaluationReport from "./evaluate/EvaluationReport";
import ComparisonTool from "./compare/ComparisonTool";
import HistoryPage from "./history/HistoryPage";
import AnalysisTool from "./analysis/AnalysisTool";
import axios from "axios";
import { API } from "../api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const TABS = [
  {
    id: "evaluate",
    label: "Evaluate Against Standards",
    shortLabel: "Evaluate",
    icon: Scales,
    color: "text-orange-600",
    border: "border-orange-600",
    bg: "bg-orange-600",
  },
  {
    id: "compare",
    label: "Compare Drawings",
    shortLabel: "Compare",
    icon: ArrowsCounterClockwise,
    color: "text-primary",
    border: "border-primary",
    bg: "bg-primary",
  },
  {
    id: "analysis",
    label: "Open Analysis",
    shortLabel: "Analyse",
    icon: ChatsCircle,
    color: "text-green-700",
    border: "border-green-700",
    bg: "bg-green-700",
  },
  {
    id: "history",
    label: "History",
    shortLabel: "History",
    icon: ClockCounterClockwise,
    color: "text-slate-500",
    border: "border-slate-500",
    bg: "bg-slate-500",
  },
];

export default function Dashboard({ sessionId, onLogout, initialMode = "evaluate", initialAnalysisFile = null, initialCompareId = null }) {
  const [mode, setMode] = useState(initialMode);
  const [pdfData, setPdfData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [analysisData, setAnalysisData] = useState(null);
  const [researchData, setResearchData] = useState(null);
  const [evaluationData, setEvaluationData] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [activeIssueIndex, setActiveIssueIndex] = useState(null);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [fasterAnalysis, setFasterAnalysis] = useState(false);

  // History-restore state
  const [viewSessionId, setViewSessionId] = useState(null);       // historical eval session
  const [restoredPageImages, setRestoredPageImages] = useState([]); // first page image from history
  const [restoreCompareId, setRestoreCompareId] = useState(initialCompareId);  // historical comparison session
  const [restoreAnalysisId, setRestoreAnalysisId] = useState(null);// historical analysis session

  const annotations = evaluationData?.annotations || [];

  const handlePDFUploaded = useCallback((data) => {
    setPdfData(data);
    setCurrentPage(1);
    // Clear any history-restore state when a new file is uploaded
    setViewSessionId(null);
    setRestoredPageImages([]);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleAnalysisComplete = useCallback((data) => {
    setAnalysisData(data);
  }, []);

  const handleResearchComplete = useCallback((data) => {
    setResearchData(data);
  }, []);

  const handleEvaluationComplete = useCallback((data) => {
    setEvaluationData(data);
    setActiveIssueIndex(null);
    setShowAnnotations(true);
    setShowReport(true);
  }, []);

  const handleAnnotationsRefined = useCallback((refinedAnnotations) => {
    setEvaluationData(prev => prev ? { ...prev, annotations: refinedAnnotations } : prev);
  }, []);

  const handleCircleClick = useCallback((issueIndex) => {
    setActiveIssueIndex(prev => prev === issueIndex ? null : issueIndex);
  }, []);

  const handleIssueClick = useCallback((issueIndex) => {
    setActiveIssueIndex(prev => prev === issueIndex ? null : issueIndex);
  }, []);

  // ── Restore a history item into its original workflow UI ────────────────────
  const handleOpenHistoryItem = useCallback(async (item) => {
    const sid = item.session_id || item.id;

    if (item._type === "evaluation") {
      // Navigate immediately so user doesn't wait on the spinner
      setEvaluationData(item.evaluation);
      setActiveIssueIndex(null);
      setShowAnnotations(true);
      setShowReport(true);
      setViewSessionId(sid);
      setPdfData({ page_count: 1, file_type: "pdf", pages: [] });
      setRestoredPageImages([]);
      setMode("evaluate");

      // Then try to fetch session metadata + first page image in the background
      try {
        const [sessRes, imgRes] = await Promise.all([
          axios.get(`${API}/sessions/${sid}`),
          axios.get(`${API}/page/${sid}/1`),
        ]);
        setPdfData({
          page_count: sessRes.data.page_count || 1,
          file_type: sessRes.data.file_type || "pdf",
          pages: [],
        });
        setRestoredPageImages([{ page: 1, image_base64: imgRes.data.image_base64 }]);
      } catch {
        // Image not available (e.g. server restarted) — report is still shown
      }
    } else if (item._type === "comparison") {
      setRestoreCompareId(sid);
      setMode("compare");
    } else if (item._type === "analysis") {
      setRestoreAnalysisId(sid);
      setMode("analysis");
    }
  }, []);

  return (
    <div className="h-full flex flex-col" data-testid="dashboard">
      {/* Header — single row */}
      <header className="bg-white border-b border-slate-200 px-5 flex-shrink-0 flex items-center justify-between h-[56px]">

        {/* Tabs */}
        <nav className="flex items-center gap-1.5" aria-label="Drawing Evaluation navigation">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = mode === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setMode(tab.id)}
                data-testid={`mode-${tab.id}-tab`}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded
                  border transition-all whitespace-nowrap select-none
                  ${active
                    ? `${tab.bg} text-white border-transparent shadow-sm`
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700"
                  }
                `}
              >
                <Icon size={13} weight={active ? "bold" : "regular"} />
                <span className="hidden lg:inline">{tab.label}</span>
                <span className="lg:hidden">{tab.shortLabel}</span>
              </button>
            );
          })}
        </nav>

        {/* Analysis mode toggle — hidden on History */}
        {mode !== "history" && (
          <TooltipProvider delayDuration={250}>
            <div
              className="flex items-center border border-slate-200 rounded overflow-hidden flex-shrink-0 bg-white"
              data-testid="faster-analysis-toggle"
            >
              {/* In-depth option */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setFasterAnalysis(false)}
                    data-testid="toggle-indepth"
                    aria-pressed={!fasterAnalysis}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all border-r border-slate-200 select-none ${
                      !fasterAnalysis
                        ? "bg-primary/10 text-primary"
                        : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Binoculars size={13} weight={!fasterAnalysis ? "fill" : "regular"} />
                    <span className="hidden sm:inline">In-depth</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[240px] leading-relaxed">
                  <p className="font-semibold text-[11px] mb-0.5">In-depth Analysis</p>
                  <p className="text-[11px] font-normal opacity-90">
                    Renders each PDF page at 2× resolution and uses an AI zoom tool to examine fine details. Most thorough — recommended for complex drawings.
                  </p>
                </TooltipContent>
              </Tooltip>

              {/* Fast option */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setFasterAnalysis(true)}
                    data-testid="toggle-fast"
                    aria-pressed={fasterAnalysis}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-all select-none ${
                      fasterAnalysis
                        ? "bg-amber-50 text-amber-700"
                        : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Lightning size={13} weight={fasterAnalysis ? "fill" : "regular"} className={fasterAnalysis ? "text-amber-500" : ""} />
                    <span className="hidden sm:inline">Fast</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[240px] leading-relaxed">
                  <p className="font-semibold text-[11px] mb-0.5">Fast Analysis</p>
                  <p className="text-[11px] font-normal opacity-90">
                    Renders each PDF page at 4× native resolution and sends the full image directly — no zooming. Quicker results, ideal for simpler drawings or a rapid first-pass review.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        )}
      </header>

      {/* Main Content */}
      {mode === "compare" ? (
        <main className="flex-1 overflow-hidden">
          <div className="h-[calc(100vh-56px)] overflow-y-auto">
            <ComparisonTool
              onBack={() => setMode("evaluate")}
              restoreSessionId={restoreCompareId}
              onStartNew={() => setRestoreCompareId(null)}
              fasterAnalysis={fasterAnalysis}
            />
          </div>
        </main>
      ) : mode === "analysis" ? (
        <main className="flex-1 overflow-hidden">
          <AnalysisTool
            onBack={() => setMode("evaluate")}
            restoreSessionId={restoreAnalysisId}
            onStartNew={() => setRestoreAnalysisId(null)}
            fasterAnalysis={fasterAnalysis}
            initialFile={initialAnalysisFile}
          />
        </main>
      ) : mode === "history" ? (
        <main className="flex-1 overflow-hidden">
          <div className="h-[calc(100vh-56px)] overflow-y-auto">
            <HistoryPage onOpenItem={handleOpenHistoryItem} />
          </div>
        </main>
      ) : (
        <main className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-0">
          {/* PDF Viewer Panel */}
          <div
            className="col-span-1 lg:col-span-8 border-r border-slate-200 bg-slate-100"
            data-testid="pdf-panel"
          >
            <PDFViewer
              sessionId={viewSessionId || sessionId}
              onPDFUploaded={handlePDFUploaded}
              pdfData={pdfData}
              onPageChange={handlePageChange}
              annotations={annotations}
              activeIssueIndex={activeIssueIndex}
              showAnnotations={showAnnotations}
              onCircleClick={handleCircleClick}
              onAnnotationsToggle={() => setShowAnnotations(v => !v)}
              restoredPageImages={restoredPageImages}
              issues={evaluationData?.issues || []}
            />
          </div>

          {/* QA / Report Panel */}
          <div
            className="col-span-1 lg:col-span-4 bg-white flex flex-col h-[calc(100vh-56px)]"
            data-testid="qa-panel"
          >
            {showReport && evaluationData ? (
              <EvaluationReport
                sessionId={viewSessionId || sessionId}
                evaluation={evaluationData}
                annotations={annotations}
                activeIssueIndex={activeIssueIndex}
                onIssueClick={handleIssueClick}
                onBack={() => { setShowReport(false); setViewSessionId(null); setRestoredPageImages([]); setPdfData(null); }}
              />
            ) : (
              <QAPanel
                sessionId={sessionId}
                pdfData={pdfData}
                currentPage={currentPage}
                analysisData={analysisData}
                researchData={researchData}
                onAnalysisComplete={handleAnalysisComplete}
                onResearchComplete={handleResearchComplete}
                onEvaluationComplete={handleEvaluationComplete}
                onAnnotationsRefined={handleAnnotationsRefined}
                fasterAnalysis={fasterAnalysis}
              />
            )}
          </div>
        </main>
      )}
    </div>
  );
}
